import { useState, useEffect } from 'react'
import { Form, Button, Row, Col, Alert, Table } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'

const BudgetLineForm = ({ line, budgetId, clienteId, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    cuenta_contable: '',
    monto_presupuestado: 0,
    prioridad: 'media',
    flexible: true,
    justificacion: '',
    distribucion_mensual: {}
  })

  const [errors, setErrors] = useState({})
  const [distribucion, setDistribucion] = useState({})

  // Obtener cuentas contables (incluir personalizadas del cliente del presupuesto si hay clienteId)
  const { data: accounts } = useQuery(
    ['chart-of-accounts', clienteId],
    async () => {
      const params = { is_active: 'true' }
      if (clienteId) params.cliente_id = clienteId
      const response = await api.get('/accounting/accounts/', { params })
      return response.data.results || response.data || []
    }
  )

  useEffect(() => {
    if (line) {
      setFormData({
        cuenta_contable: line.cuenta_contable || line.cuenta_contable_id || '',
        monto_presupuestado: line.monto_presupuestado || 0,
        prioridad: line.prioridad || 'media',
        flexible: line.flexible !== undefined ? line.flexible : true,
        justificacion: line.justificacion || '',
        distribucion_mensual: line.distribucion_mensual || {}
      })
      setDistribucion(line.distribucion_mensual || {})
    } else {
      // Inicializar distribución mensual por defecto (igual para todos los meses)
      const defaultDist = {}
      for (let i = 1; i <= 12; i++) {
        defaultDist[i.toString()] = (100 / 12).toFixed(2)
      }
      setDistribucion(defaultDist)
    }
  }, [line])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleDistribucionChange = (mes, valor) => {
    const newDistribucion = { ...distribucion }
    newDistribucion[mes.toString()] = parseFloat(valor) || 0
    setDistribucion(newDistribucion)
  }

  const distribuirEquitativamente = () => {
    const meses = 12
    const porcentaje = (100 / meses).toFixed(2)
    
    const newDistribucion = {}
    for (let i = 1; i <= meses; i++) {
      newDistribucion[i.toString()] = porcentaje
    }
    setDistribucion(newDistribucion)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const newErrors = {}
    if (!formData.cuenta_contable) newErrors.cuenta_contable = 'Cuenta contable es requerida'
    if (!formData.monto_presupuestado || formData.monto_presupuestado <= 0) {
      newErrors.monto_presupuestado = 'Monto debe ser mayor a 0'
    }
    
    // Validar que la distribución sume 100%
    const totalDistribucion = Object.values(distribucion).reduce((sum, val) => sum + parseFloat(val || 0), 0)
    if (Math.abs(totalDistribucion - 100) > 0.01) {
      newErrors.distribucion = 'La distribución mensual debe sumar 100%'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    const submitData = {
      ...formData,
      distribucion_mensual: distribucion,
      presupuesto: budgetId
    }
    
    if (line) {
      submitData.id = line.id
    }
    
    onSubmit(submitData)
  }

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ]

  const totalDistribucion = Object.values(distribucion).reduce((sum, val) => sum + parseFloat(val || 0), 0)

  return (
    <Form onSubmit={handleSubmit}>
      {errors.distribucion && <Alert variant="danger">{errors.distribucion}</Alert>}
      
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cuenta Contable *</Form.Label>
            <Form.Select
              name="cuenta_contable"
              value={formData.cuenta_contable}
              onChange={handleChange}
              isInvalid={!!errors.cuenta_contable}
              required
            >
              <option value="">Seleccionar cuenta...</option>
              {accounts?.map(account => {
                const accountId = account.cuenta_base_id ?? account.id
                return (
                  <option key={account.id ?? accountId} value={accountId}>
                    {account.code} - {account.name}{account.is_personalized ? ' (Personalizado)' : ''}
                  </option>
                )
              })}
            </Form.Select>
            {errors.cuenta_contable && <Form.Control.Feedback type="invalid">{errors.cuenta_contable}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Monto Presupuestado *</Form.Label>
            <Form.Control
              type="number"
              min="0.01"
              step="0.01"
              name="monto_presupuestado"
              value={formData.monto_presupuestado}
              onChange={handleChange}
              isInvalid={!!errors.monto_presupuestado}
              required
            />
            {errors.monto_presupuestado && <Form.Control.Feedback type="invalid">{errors.monto_presupuestado}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Prioridad</Form.Label>
            <Form.Select
              name="prioridad"
              value={formData.prioridad}
              onChange={handleChange}
            >
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="critica">Crítica</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mt-4">
            <Form.Check
              type="checkbox"
              name="flexible"
              label="Flexible (permite modificaciones sin aprobación)"
              checked={formData.flexible}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Justificación</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="justificacion"
              value={formData.justificacion}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Distribución mensual */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5>Distribución Mensual (%)</h5>
          <Button variant="outline-secondary" size="sm" onClick={distribuirEquitativamente}>
            Distribuir Equitativamente
          </Button>
        </div>
        <Table responsive size="sm">
          <thead>
            <tr>
              {meses.map((mes, index) => (
                <th key={index + 1}>{mes}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {meses.map((mes, index) => {
                const mesNum = (index + 1).toString()
                return (
                  <td key={mesNum}>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={distribucion[mesNum] || ''}
                      onChange={(e) => handleDistribucionChange(mesNum, e.target.value)}
                      size="sm"
                    />
                  </td>
                )
              })}
            </tr>
          </tbody>
        </Table>
        <Alert variant={Math.abs(totalDistribucion - 100) < 0.01 ? 'success' : 'warning'}>
          Total: {totalDistribucion.toFixed(2)}% {Math.abs(totalDistribucion - 100) < 0.01 ? '✓' : '(debe ser 100%)'}
        </Alert>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : (line ? 'Actualizar' : 'Crear')}
        </Button>
      </div>
    </Form>
  )
}

export default BudgetLineForm

